import { isBefore } from 'date-fns';

import { DateFields } from '../entities/date-range.entity';

export type DatePart = Pick<DateFields, 'Year' | 'Month' | 'Day'>;

/**
 * A date part is complete unless a finer field is set without its coarser field(s): a day
 * without a year/month, or a month without a year. An entirely empty part is complete — it
 * stands for an open-ended bound. Mirrors the advanced-search date filter's completeness rule.
 */
export function isCompleteDatePart(part: DatePart): boolean {
    const hasYear: boolean = part.Year !== null && part.Year !== undefined;
    const hasMonth: boolean = part.Month !== null && part.Month !== undefined;
    const hasDay: boolean = part.Day !== null && part.Day !== undefined;

    return !((hasDay && (!hasYear || !hasMonth)) || (hasMonth && !hasYear));
}

/**
 * True when `from` is strictly earlier than `to`, compared at whatever granularity is filled in.
 * Strict (equal ranges are not "before") to match the advanced-search date filter's from-before-to
 * rule. Uses the same `setFullYear` construction so years 1-99 aren't shifted into the 1900s.
 */
export function isFromBeforeTo(from: DatePart, to: DatePart): boolean {
    return isBefore(toComparableDate(from), toComparableDate(to));
}

function toComparableDate(part: DatePart): Date {
    const year: number = part.Year ?? 1;
    const date: Date = new Date(year, part.Month ? part.Month - 1 : 0, part.Day ?? 1);
    date.setFullYear(year);
    return date;
}
